// Parallel dialing (ADR 0010): the admin console section. Shapes match
// crates/api-types/src/pdial.rs (docs/contract.md section 19).
import { queryOptions } from "@tanstack/react-query";
import type {
  PdialComplianceMode,
  PdialCompliancePatch,
  PdialComplianceView,
  PdialMessageEdit,
  PdialModeAcknowledge,
  PdialModesView,
  PdialOfferingCreate,
  PdialOfferingPatch,
} from "@vd/api-client";
import companyNames from "../../../../crates/core/src/pdial/company_names.json";
import { api } from "./client";

export type SecondAnswerMode = "silent" | "message";
export type DialMode = "parallel" | "power";
export type MeterState = "normal" | "reduced" | "forced_single";
export type Reason =
  | "not_enabled"
  | "not_acknowledged"
  | "not_granted"
  | "rep_daily_limit"
  | "company_daily_limit"
  | "abandonment_meter"
  | "telnyx_capacity"
  | "state_rules_only"
  | "no_mode"
  | "bridge_slow"
  | "rep_abandon_limit";

export interface Source {
  label: string;
  url: string;
}
export interface Section {
  id: string;
  title: string;
  paragraphs: string[];
  sources: Source[];
}
export interface AckItem {
  id: string;
  /** always, silent, message, callback_assistant or cells_in_batches */
  required_for: string;
  text: string;
  url: string;
}
export interface LearnContent {
  title: string;
  intro: string;
  sections: Section[];
  acknowledgments: AckItem[];
  agreement: string;
}
export interface LearnView {
  content: LearnContent;
  agreement: string;
  content_version: string;
  company: string;
}

export interface Availability {
  available: boolean;
  reason: Reason | null;
  message: string;
  resets_at: string | null;
  used: number | null;
  limit: number | null;
}

export interface CooldownSettings {
  cancelled_mins: number;
  no_answer_mins: number;
  machine_mins: number;
  busy_mins: number;
  abandoned_mins: number;
}

export interface PdialPolicy {
  max_lines: number;
  abandon_target_pct: number;
  reduce_above_pct: number;
  force_single_above_pct: number;
  recover_below_pct: number;
  min_live_answers: number;
  noisy_max_lines: number;
  ring_timeout_secs: number;
  voicemail_action_secs: number;
  max_attempts_per_day: number;
  cooldowns: CooldownSettings;
  rep_daily_limit: number | null;
  company_daily_limit: number | null;
  daily_cap_per_number: number;
}

export interface StateRule {
  state: string;
  single_line: boolean;
  no_message: boolean;
  max_attempts_24h: number | null;
  no_sunday: boolean;
  /** YYYY-MM-DD dates with no calls (state holidays). */
  no_call_dates: string[];
}

export interface AcknowledgmentView {
  id: string;
  user_id: string;
  user_name: string | null;
  acknowledged_at: string;
  content_version: string;
  ip: string | null;
  user_agent: string | null;
  items: string[];
}

export interface CapacityView {
  /** null: not limited (Verified accounts publish no limit). */
  concurrency: number | null;
  /** Dials Telnyx refused for capacity in the last 7 days. */
  refusals_7d: number;
  source: string;
  /** unknown, trial, paid, verified or enterprise (Telnyx PTPVE, confirmed by an admin). */
  tier: string;
  checked_at: string | null;
  error: string | null;
  calls_per_day: number | null;
  calls_today: number;
  calls_per_hour: number | null;
  calls_this_hour: number;
  needed_now: number;
  needed_for_all: number;
}

export interface RepLimitView {
  user_id: string;
  name: string;
  email: string | null;
  granted: boolean;
  daily_limit: number | null;
  effective_limit: number | null;
  used_today: number;
  in_session: boolean;
  mode: DialMode | null;
  availability: Availability;
}

export interface MeterView {
  live_answers: number;
  abandoned: number;
  rate_pct: number;
  state: MeterState;
  allowed_lines: number;
  projected_ok_at: string | null;
}

export interface CampaignMeter extends MeterView {
  meter_key: string;
  name: string;
  kind: string;
}

export interface LimitsView {
  time_zone: string;
  resets_at: string;
  company_limit: number | null;
  company_used_today: number;
  rep_default_limit: number | null;
}

export interface PoolNumber {
  id: string;
  e164: string;
  label: string;
  pool_brand_id: string | null;
  brand_name: string | null;
  dials_today: number;
  daily_cap: number;
  rested_until: string | null;
  rest_reason: string | null;
  answer_rate_7d: number | null;
}

export interface LatencyStage {
  stage: string;
  p50_ms: number | null;
  p95_ms: number | null;
  samples: number;
}

export interface VariantStats {
  offset_ms: number;
  answered_batches: number;
  collisions: number;
  collision_rate: number;
  collision_low: number;
  collision_high: number;
  connects_per_hour: number | null;
  median_time_to_connect_ms: number | null;
}

export interface HardCheck {
  id: string;
  ok: boolean;
  message: string;
  rule: string;
  url: string;
}
export interface AiWarning {
  kind: string;
  quote: string;
  explanation: string;
  rule: string;
  url: string;
}
export interface TextCheckView {
  id: string;
  kind: "message" | "instructions";
  text: string;
  text_version: string;
  rendered: string | null;
  hard_checks: HardCheck[];
  passed: boolean;
  ai: { warnings: AiWarning[]; proposed: string | null; model: string } | null;
  ai_error: string | null;
  created_at: string;
}

export interface PdialAdmin {
  enabled: boolean;
  enabled_at: string | null;
  /** The compliance mode (ADR 0039); null until an admin chooses one. */
  pdial_mode?: PdialComplianceMode | null;
  /** The chosen mode is acknowledged at the current content version. */
  mode_acknowledged?: boolean;
  company: string;
  policy: PdialPolicy;
  safe_harbor_text: string;
  safe_harbor_default: string;
  safe_harbor_rendered: string;
  /** The company name the message says, when set; null: message_company_default. */
  message_company: string | null;
  message_company_default: string;
  /** Why the message can't play as set, when so. */
  message_problem: string | null;
  /** Set when migration 0099 moved second answers to silent and no admin has saved since. */
  migration_notice: string | null;
  safe_harbor_voice: string;
  callback_number: string | null;
  content_version: string;
  acknowledgment: AcknowledgmentView | null;
  needs_reacknowledgment: boolean;
  /** Absent without the Telnyx grant (ADR 0050); then latency, experiment and
   * start_offset_variants are empty and cancel_latency_ms is null. */
  capacity?: CapacityView;
  limits: LimitsView;
  reps: RepLimitView[];
  state_rules: StateRule[];
  meters: CampaignMeter[];
  pool: PoolNumber[];
  second_answer_mode: SecondAnswerMode;
  callback_assistant: boolean;
  callback_instructions: string | null;
  callback_voice_available: boolean;
  cells_in_batches: boolean;
  voip_as_cell: boolean;
  start_offset_variants: number[];
  required_items: string[];
  acked_items: string[];
  latency: LatencyStage[];
  experiment: VariantStats[];
  experiment_sample_size: number;
  last_message_check: TextCheckView | null;
  last_instructions_check: TextCheckView | null;
  setup_missing: string[];
}

export interface SettingsPatch {
  enabled?: boolean;
  policy?: PdialPolicy;
  safe_harbor_text?: string | null;
  safe_harbor_voice?: string;
  callback_number?: string | null;
  /** The company name the message says; null: the brand, else the tenant's name. */
  message_company?: string | null;
  confirmed_concurrency?: number | null;
  calls_per_day?: number | null;
  calls_per_hour?: number | null;
  telnyx_tier?: string;
  second_answer_mode?: SecondAnswerMode;
  callback_assistant?: boolean;
  callback_instructions?: string | null;
  cells_in_batches?: boolean;
  voip_as_cell?: boolean;
  start_offset_variants?: number[];
}

export interface AttemptRecord {
  id: string;
  session_id: string;
  user_id: string;
  rep_name: string | null;
  batch: number;
  batch_size: number;
  campaign_id: string | null;
  list_id: string | null;
  contact_name: string | null;
  company: string | null;
  phone: string;
  from_e164: string | null;
  line_type: string;
  state_code: string | null;
  dialed_at: string;
  answered_at: string | null;
  amd_verdict: string | null;
  outcome: string | null;
  hangup_cause: string | null;
  message_started_at: string | null;
  opted_out_at: string | null;
  opted_out_via: string | null;
  call_id: string | null;
  cancel_latency_ms: number | null;
  answer_to_bridge_ms: number | null;
  ended_at: string | null;
}

export const pdialKeys = {
  admin: ["admin", "pdial"] as const,
  compliance: ["admin", "pdial", "compliance"] as const,
  modes: ["admin", "pdial", "modes"] as const,
  learn: ["admin", "pdial", "learn"] as const,
  attempts: ["admin", "pdial", "attempts"] as const,
};

export const pdialQuery = queryOptions({
  queryKey: pdialKeys.admin,
  queryFn: () => api<PdialAdmin>("/v1/admin/pdial"),
});

export const complianceQuery = queryOptions({
  queryKey: pdialKeys.compliance,
  queryFn: () => api<PdialComplianceView>("/v1/admin/pdial/compliance"),
});

export const modesQuery = queryOptions({
  queryKey: pdialKeys.modes,
  queryFn: () => api<PdialModesView>("/v1/admin/pdial/modes"),
  staleTime: 5 * 60_000,
});

/** The compliance mode endpoints (ADR 0039). Every write returns the whole view. */
export const pdialModes = {
  acknowledge: (mode: PdialComplianceMode, body: PdialModeAcknowledge) =>
    api<PdialComplianceView>(`/v1/admin/pdial/modes/${mode}/acknowledge`, { method: "POST", body }),
  save: (body: PdialCompliancePatch) =>
    api<PdialComplianceView>("/v1/admin/pdial/compliance", { method: "PUT", body }),
  createOffering: (body: PdialOfferingCreate) =>
    api<PdialComplianceView>("/v1/admin/pdial/offerings", { method: "POST", body }),
  patchOffering: (id: string, body: PdialOfferingPatch) =>
    api<PdialComplianceView>(`/v1/admin/pdial/offerings/${id}`, { method: "PATCH", body }),
  attest: (id: string, body: PdialModeAcknowledge) =>
    api<PdialComplianceView>(`/v1/admin/pdial/offerings/${id}/attestation`, {
      method: "POST",
      body,
    }),
  revoke: (id: string) =>
    api<PdialComplianceView>(`/v1/admin/pdial/offerings/${id}/attestation`, { method: "DELETE" }),
  setCampaignOffering: (campaignId: string, offering_id: string | null) =>
    api<PdialComplianceView>(`/v1/admin/pdial/campaigns/${campaignId}/offering`, {
      method: "PUT",
      body: { offering_id },
    }),
  draftMessage: (brand_id: string | null) =>
    api<PdialComplianceView>("/v1/admin/pdial/messages/draft", {
      method: "POST",
      body: { brand_id },
    }),
  editMessage: (id: string, body: PdialMessageEdit) =>
    api<PdialComplianceView>(`/v1/admin/pdial/messages/${id}`, { method: "PUT", body }),
  approveMessage: (id: string) =>
    api<PdialComplianceView>(`/v1/admin/pdial/messages/${id}/approve`, { method: "POST" }),
};

export const learnQuery = queryOptions({
  queryKey: pdialKeys.learn,
  queryFn: () => api<LearnView>("/v1/admin/pdial/learn"),
  staleTime: 5 * 60_000,
});

export const attemptsQuery = queryOptions({
  queryKey: pdialKeys.attempts,
  queryFn: () => api<{ items: AttemptRecord[] }>("/v1/admin/pdial/attempts", { query: { limit: 100 } }),
});

export const pdial = {
  acknowledge: (content_version: string, items: string[]) =>
    api<AcknowledgmentView>("/v1/admin/pdial/acknowledge", {
      method: "POST",
      body: { content_version, items, agree: true },
    }),
  putSettings: (p: SettingsPatch) => api<PdialAdmin>("/v1/admin/pdial/settings", { method: "PUT", body: p }),
  grant: (userId: string, granted: boolean, daily_limit: number | null) =>
    api<PdialAdmin>(`/v1/admin/pdial/grants/${userId}`, {
      method: "PUT",
      body: { granted, daily_limit },
    }),
  putStateRule: (r: StateRule) => api<PdialAdmin>("/v1/admin/pdial/state-rules", { method: "PUT", body: r }),
  deleteStateRule: (state: string) =>
    api<PdialAdmin>(`/v1/admin/pdial/state-rules/${state}`, { method: "DELETE" }),
  putPool: (id: string, body: { pool_brand_id?: string | null; rested_until?: string | null }) =>
    api<PdialAdmin>(`/v1/admin/pdial/pool/${id}`, { method: "PUT", body }),
  checkCapacity: () => api<PdialAdmin>("/v1/admin/pdial/capacity/check", { method: "POST" }),
  check: (kind: "message" | "instructions", text: string, opt_out_number?: string | null) =>
    api<TextCheckView>("/v1/admin/pdial/check", {
      method: "POST",
      body: { kind, text, opt_out_number },
    }),
};

/** The acknowledgment items a configuration needs (mirrors vd_core::pdial::content). */
export function requiredItems(
  items: AckItem[],
  mode: SecondAnswerMode,
  assistant: boolean,
  cells: boolean,
): AckItem[] {
  return items.filter((a) => {
    switch (a.required_for) {
      case "always":
        return true;
      case "silent":
        return mode === "silent";
      case "message":
        return mode === "message";
      case "callback_assistant":
        return mode === "message" && assistant;
      case "cells_in_batches":
        return mode === "silent" && cells;
      default:
        return false;
    }
  });
}

/** "{company}" filled in. */
export function fill(text: string, company: string): string {
  return text.split("{company}").join(company);
}

/** The Wilson interval as "0.38% to 0.66%". */
export function pct(x: number, digits = 2): string {
  return `${(x * 100).toFixed(digits)}%`;
}

/** The fixed disclosure message (vd_core::pdial::policy::DEFAULT_SAFE_HARBOR). */
export const DISCLOSURE_TEMPLATE =
  "Hello, this is {company}. We called you for telemarketing purposes. To ask us not to call again, call {callback_number}, or press 9 now.";

const BANNED = new Set<string>(companyNames.banned_words);
const BANNED_PHRASES: string[] = companyNames.banned_phrases;
const ABBREVIATIONS = new Set<string>(companyNames.abbreviations);

/** A to Z, or a Latin letter with a diacritic (U+00C0 to U+017F without U+00D7 and U+00F7). */
const LETTER = "A-Za-z\\u00C0-\\u00D6\\u00D8-\\u00F6\\u00F8-\\u017F";
const NAME_CHARS = new RegExp(`^[${LETTER}0-9 &'.-]+$`, "u");
const HAS_LETTER = new RegExp(`[${LETTER}]`, "u");
const INITIALS = new RegExp(`^([${LETTER}]\\.)+$`, "u");

/** Letters without an ASCII base after decomposition (vd_core::pdial::policy::NAME_FOLD). */
const FOLD = new Map<string, string>([
  ["\u00C6", "ae"],
  ["\u00E6", "ae"],
  ["\u00D0", "d"],
  ["\u00F0", "d"],
  ["\u00D8", "o"],
  ["\u00F8", "o"],
  ["\u00DE", "th"],
  ["\u00FE", "th"],
  ["\u00DF", "ss"],
  ["\u0110", "d"],
  ["\u0111", "d"],
  ["\u0126", "h"],
  ["\u0127", "h"],
  ["\u0131", "i"],
  ["\u0138", "k"],
  ["\u0141", "l"],
  ["\u0142", "l"],
  ["\u014A", "n"],
  ["\u014B", "n"],
  ["\u0152", "oe"],
  ["\u0153", "oe"],
  ["\u0166", "t"],
  ["\u0167", "t"],
]);

/** The name as lower-case ASCII for the word checks (vd_core::pdial::policy::name_fold). */
export function nameFold(n: string): string {
  let out = "";
  for (const c of n.normalize("NFD").replace(/[\u0300-\u036f]/g, "")) {
    out += FOLD.get(c) ?? (/[A-Z]/.test(c) ? c.toLowerCase() : c);
  }
  return out;
}

/**
 * The two readings the word checks use (vd_core::pdial::policy::name_words): split at every
 * mark, and each word with its marks removed and runs of single letters joined.
 */
export function nameWords(n: string): [string, string] {
  const f = nameFold(n);
  const split = f
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .join(" ");
  const joined: string[] = [];
  let run = "";
  for (const w of f.split(" ")) {
    const t = w.replace(/[^a-z0-9]/g, "");
    if (!t) continue;
    if (/^[a-z]$/.test(t)) run += t;
    else {
      if (run) joined.push(run);
      run = "";
      joined.push(t);
    }
  }
  if (run) joined.push(run);
  return [split, joined.join(" ")];
}

/** O read as zero in a run of digits and O's that holds a digit (name_digits). */
export function nameDigits(n: string): string {
  return n.replace(/[0-9oO]+/g, (run) => (/[0-9]/.test(run) ? run.replace(/[oO]/g, "0") : run));
}

/**
 * Digits said one at a time read as one number (name_number): the separators between two runs
 * of digits are dropped when either run is a single digit ("8-0-0" reads "800").
 */
export function nameNumber(n: string): string {
  return n.replace(/(?<![0-9])([0-9])[ .-]+(?=[0-9])|([0-9])[ .-]+(?=[0-9](?:[^0-9]|$))/g, "$1$2");
}

/** The name as the phone check reads it (name_phone_text): folded, O as zero, digits joined. */
export function namePhoneText(n: string): string {
  return nameNumber(nameDigits(nameFold(n)));
}

/**
 * The company name as the message says it, or why not. The same rule as
 * vd_core::pdial::policy::validate_company_name and the database check; the cases in
 * crates/core/src/pdial/company_names.json run in all three. The server decides.
 */
export function validateCompanyName(
  name: string,
): { ok: true; value: string } | { ok: false; error: string } {
  const n = name.normalize("NFKC").trim().split(/\s+/).filter(Boolean).join(" ");
  const len = [...n].length;
  if (len < 2 || len > 60) return { ok: false, error: "The company name must be 2 to 60 characters." };
  if (!NAME_CHARS.test(n))
    return {
      ok: false,
      error: "Use letters (accents are fine), digits, spaces and & ' - in the company name.",
    };
  if (!HAS_LETTER.test(n)) return { ok: false, error: "The company name needs at least one letter." };
  const words = n.split(" ");
  if (words.length > 6) return { ok: false, error: "Use at most 6 words in the company name." };
  if (words.some((w) => w.includes(".") && !ABBREVIATIONS.has(w.toLowerCase()) && !INITIALS.test(w)))
    return {
      ok: false,
      error:
        "A period only in an abbreviation such as Inc., Co., St. or Intl., or an initial: the name is not a sentence.",
    };
  if (phoneLike(n))
    return {
      ok: false,
      error: "No phone numbers in the company name (a number in the name is fine).",
    };
  const banned = (s: string) =>
    s.split(" ").some((w) => BANNED.has(w)) || BANNED_PHRASES.some((p) => ` ${s} `.includes(` ${p} `));
  if (nameWords(n).some(banned))
    return {
      ok: false,
      error:
        "The company name can't spell out a web address or carry offer, price, claim or instruction words; use the name people know.",
    };
  return { ok: true, value: n };
}

/**
 * Phone-like content in a name (vd_core::pdial::policy::phone_like), read on the folded name
 * with O read as zero next to digits and single digits joined: a run of 5 or more digits, 7 or
 * more digits in all, a toll-free code with or without its 1 and after any zeros (800 Flowers,
 * 1-800, 1 888, 8-0-0, O8OO...), an area code and exchange (816 555 PRNT, 816 - 555), or an
 * extension (555 x12, ext 12). Numbers in names (570 Apparel, 3M, 21st Century Prints) are
 * fine.
 */
export function phoneLike(name: string): boolean {
  const n = namePhoneText(name);
  return (
    /[0-9]{5}/.test(n) ||
    n.replace(/[^0-9]/g, "").length >= 7 ||
    /(^|[^0-9])0*(1[ .-]?)?8(00|33|44|55|66|77|88)([^0-9]|$)/.test(n) ||
    /(^|[^0-9])[0-9]{3}[ .-]+[0-9]{3}([^0-9]|$)/.test(n) ||
    /[0-9][ .-]*(x|ext)\.? ?[0-9]/i.test(n) ||
    /(^| )ext ?[0-9]/i.test(n)
  );
}

/**
 * The ten-digit NANP numbers in a text, digits separated by spaces, dots, dashes or
 * parentheses (vd_core::pdial::compliance::phone_numbers).
 */
export function phoneNumbers(text: string): string[] {
  const out: string[] = [];
  let digits = "";
  const flush = () => {
    const core = digits.startsWith("1") && digits.length === 11 ? digits.slice(1) : digits;
    if (core.length === 10) out.push(core);
    digits = "";
  };
  for (const ch of text) {
    if (/[0-9]/.test(ch)) digits += ch;
    else if (/[ .()+-]/.test(ch) && digits.length > 0) {
      // A separator inside a number.
    } else flush();
  }
  flush();
  return out;
}

/** The opt-out number: +1 and a US number people can call (not 900 or 976). */
export function validateOptOutNumber(
  e164: string,
): { ok: true; value: string } | { ok: false; error: string } {
  const t = e164.trim();
  const m = /^\+1(\d{10})$/.exec(t);
  const d = m?.[1];
  if (d && /^[2-9]\d{2}[2-9]/.test(d) && !d.startsWith("900") && !d.startsWith("976"))
    return { ok: true, value: `+1${d}` };
  return {
    ok: false,
    error: "The opt-out number must be a US number people can call, as +18165550100.",
  };
}

/** The message as it plays, when both fields are valid and it gives no other number. */
export function renderDisclosure(company: string, optOut: string): string | null {
  const c = validateCompanyName(company);
  const n = validateOptOutNumber(optOut);
  if (!c.ok || !n.ok) return null;
  const d = n.value.slice(2);
  const text = DISCLOSURE_TEMPLATE.split("{company}")
    .join(c.value)
    .split("{callback_number}")
    .join(`${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`);
  const numbers = phoneNumbers(text);
  return numbers.length === 1 && numbers[0] === d ? text : null;
}
